import auth from '@/auth/auth';
import {
    DeactivatePasswordLoginDocument,
    DeactivateSsoLoginDocument,
    DeactivateUserDocument,
    GetUserWithLoginDataDocument,
    SsoProvider,
    UpdateUserIsAdminDocument,
} from '@/generated/graphql';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { Ref, computed } from 'vue';
import { mapUser } from '../user';
import { invalidateUserList } from '../userListCache';

type LoginMethods = SsoProvider | 'password';

export function useUser(userId: Ref<string>) {
    const fluent = useFluent();
    const isAdmin = auth.isAdmin();

    const getUserQuery = useQuery(
        GetUserWithLoginDataDocument,
        () => ({ userId: userId.value }),
        () => ({ enabled: !!userId.value }),
    );

    const user = computed(() => {
        const userData = getUserQuery.result.value?.userWithLoginData;
        if (userData) {
            return mapUser(userData);
        }
    });

    const editDisabledExplanation = computed(() => {
        return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
    });

    const deactivateMutation = useMutation(DeactivateUserDocument, { update: invalidateUserList });

    async function deactivateUser() {
        await deactivateMutation.mutate({ userId: userId.value });
    }

    const loginMethodsWithActiveStatus = computed(() => {
        let methods: Map<LoginMethods, boolean> = new Map();
        if (!user.value) return methods;
        for (const sso of user.value.ssoLogins) {
            if (sso.isActive) {
                methods.set(sso.provider, sso.isActive);
            }
        }
        if (user.value.passwordLogin) {
            methods.set('password', user.value.passwordLogin.isActive);
        }
        return methods;
    });

    const updateUserIsAdminMutation = useMutation(UpdateUserIsAdminDocument, { update: invalidateUserList });

    async function updateUserIsAdmin(isAdmin: boolean) {
        if (!user.value) return;
        await updateUserIsAdminMutation.mutate({ userId: user.value.id, isAdmin });
    }

    const deactivatePasswordLoginMutation = useMutation(DeactivatePasswordLoginDocument, {
        update: invalidateUserList,
    });

    async function deactivateUserPasswordLogin() {
        if (!user.value) return;
        await deactivatePasswordLoginMutation.mutate({ userId: user.value.id });
    }

    const deactivateSsoLoginMutation = useMutation(DeactivateSsoLoginDocument, { update: invalidateUserList });

    async function deactivateUserSsoLogin(provider: SsoProvider) {
        if (!user.value) return;
        const ssoLogin = user.value.ssoLogins.find((sso) => sso.provider === provider);
        if (ssoLogin) {
            await deactivateSsoLoginMutation.mutate({ userId: user.value.id, provider, oid: ssoLogin.oid });
        }
    }

    return {
        user,
        loginMethodsWithActiveStatus,
        getUserQuery,
        canEdit: isAdmin,
        canDelete: isAdmin,
        editDisabledExplanation,
        updateUserIsAdmin,
        deactivateUser,
        deactivateUserPasswordLogin,
        deactivateUserSsoLogin,
    };
}
